import React, { useState, useEffect, useCallback, Suspense } from 'react';
import { purchaseAPI, itemsAPI } from '../lib/api';
import { formatCurrency, formatDateTime, getApprovalStatusColor } from '../lib/utils';
import { Card, CardContent } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Loader2, ShoppingCart, Archive } from 'lucide-react';
const CommentSection = React.lazy(() => import('../components/CommentSection'));

// 21 Oct 2026: Offers & Purchases is retired. This page is now a Super-Admin-only,
// read-only history of purchases created before retirement — the "Add Purchase"
// action has been removed.
export default function PurchasesPage() {
  const [purchases, setPurchases] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [skuFilter, setSkuFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedPurchase, setSelectedPurchase] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (skuFilter && skuFilter !== 'all') params.sku_id = skuFilter;
      if (statusFilter && statusFilter !== 'all') params.approval_status = statusFilter;
      
      const [purchaseRes, itemsRes] = await Promise.all([
        purchaseAPI.list(params),
        itemsAPI.list({ limit: 500 }),
      ]);
      setPurchases(purchaseRes.data);
      setItems(itemsRes.data.items || []);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [skuFilter, statusFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return (
    <div className="space-y-6" data-testid="purchases-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Purchases</h1>
          <p className="text-muted-foreground text-sm flex items-center gap-1.5">
            <Archive className="h-3.5 w-3.5" />Retired — read-only history, Super Admin only
          </p>
        </div>
      </div>

      <Card className="rounded-sm">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <Select value={skuFilter} onValueChange={setSkuFilter}>
              <SelectTrigger className="w-full sm:w-48 rounded-sm">
                <SelectValue placeholder="All Items" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Items</SelectItem>
                {items.filter(item => item.sku).map((item) => (
                  <SelectItem key={item.zoho_item_id} value={item.sku}>{item.sku}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-48 rounded-sm">
                <SelectValue placeholder="All Approval Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="NOT_REQUIRED">Not Required</SelectItem>
                <SelectItem value="PENDING">Pending</SelectItem>
                <SelectItem value="APPROVED">Approved</SelectItem>
                <SelectItem value="REJECTED">Rejected</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <Card className="rounded-sm">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/50">
                <TableHead className="font-medium text-xs uppercase">Date</TableHead>
                <TableHead className="font-medium text-xs uppercase">SKU</TableHead>
                <TableHead className="font-medium text-xs uppercase">Supplier</TableHead>
                <TableHead className="font-medium text-xs uppercase">Country</TableHead>
                <TableHead className="font-medium text-xs uppercase text-right">Qty</TableHead>
                <TableHead className="font-medium text-xs uppercase text-right">Unit Price</TableHead>
                <TableHead className="font-medium text-xs uppercase text-right">Total</TableHead>
                <TableHead className="font-medium text-xs uppercase">Approval</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin mx-auto text-muted-foreground" />
                  </TableCell>
                </TableRow>
              ) : purchases.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8">
                    <ShoppingCart className="h-12 w-12 mx-auto text-muted-foreground/50 mb-2" />
                    <p className="text-muted-foreground">No purchases found</p>
                  </TableCell>
                </TableRow>
              ) : (
                purchases.map((purchase) => (
                  <TableRow
                    key={purchase.id}
                    className="table-row-hover cursor-pointer"
                    onClick={() => setSelectedPurchase(purchase)}
                    data-testid={`purchase-row-${purchase.id}`}
                  >
                    <TableCell className="text-sm">{formatDateTime(purchase.date_time)}</TableCell>
                    <TableCell className="font-mono font-medium">{purchase.sku_code}</TableCell>
                    <TableCell>{purchase.supplier_name}</TableCell>
                    <TableCell>{purchase.supplier_country || '-'}</TableCell>
                    <TableCell className="font-mono text-right">{purchase.qty}</TableCell>
                    <TableCell className="font-mono text-right">{formatCurrency(purchase.unit_price_usd)}</TableCell>
                    <TableCell className="font-mono text-right font-medium">
                      {formatCurrency(purchase.qty * purchase.unit_price_usd)}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={`rounded-sm ${getApprovalStatusColor(purchase.approval_status)}`}>
                        {purchase.approval_status.replace(/_/g, ' ')}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Purchase Detail Dialog with Comments */}
      <Dialog open={!!selectedPurchase} onOpenChange={() => setSelectedPurchase(null)}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Purchase Detail</DialogTitle>
            <DialogDescription>{selectedPurchase?.sku_code} — {selectedPurchase?.supplier_name}</DialogDescription>
          </DialogHeader>
          {selectedPurchase && (
            <div className="space-y-4" data-testid="purchase-detail">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="p-3 bg-muted rounded-lg">
                  <p className="text-xs text-muted-foreground">SKU</p>
                  <p className="text-sm font-semibold font-mono">{selectedPurchase.sku_code}</p>
                </div>
                <div className="p-3 bg-muted rounded-lg">
                  <p className="text-xs text-muted-foreground">Supplier</p>
                  <p className="text-sm font-semibold">{selectedPurchase.supplier_name}</p>
                </div>
                <div className="p-3 bg-muted rounded-lg">
                  <p className="text-xs text-muted-foreground">Quantity</p>
                  <p className="text-sm font-semibold font-mono">{selectedPurchase.qty}</p>
                </div>
                <div className="p-3 bg-muted rounded-lg">
                  <p className="text-xs text-muted-foreground">Unit Price</p>
                  <p className="text-sm font-semibold font-mono">{formatCurrency(selectedPurchase.unit_price_usd)}</p>
                </div>
                <div className="p-3 bg-muted rounded-lg">
                  <p className="text-xs text-muted-foreground">Total</p>
                  <p className="text-sm font-semibold font-mono text-emerald-600">{formatCurrency(selectedPurchase.qty * selectedPurchase.unit_price_usd)}</p>
                </div>
                <div className="p-3 bg-muted rounded-lg">
                  <p className="text-xs text-muted-foreground">Country</p>
                  <p className="text-sm font-semibold">{selectedPurchase.supplier_country || '-'}</p>
                </div>
                <div className="p-3 bg-muted rounded-lg">
                  <p className="text-xs text-muted-foreground">Date</p>
                  <p className="text-sm font-semibold">{formatDateTime(selectedPurchase.date_time)}</p>
                </div>
                <div className="p-3 bg-muted rounded-lg">
                  <p className="text-xs text-muted-foreground">Approval</p>
                  <Badge variant="outline" className={`rounded-sm mt-1 ${getApprovalStatusColor(selectedPurchase.approval_status)}`}>
                    {selectedPurchase.approval_status.replace(/_/g, ' ')}
                  </Badge>
                </div>
              </div>
              {selectedPurchase.notes && (
                <div className="p-3 bg-muted rounded-lg">
                  <p className="text-xs text-muted-foreground mb-1">Notes</p>
                  <p className="text-sm">{selectedPurchase.notes}</p>
                </div>
              )}
              <Suspense fallback={<div className="py-4 text-center text-muted-foreground text-sm">Loading comments...</div>}>
                <CommentSection entityType="purchase" entityId={selectedPurchase.id} title="Purchase Comments" />
              </Suspense>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
